import { forwardRef, useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  CoachMark,
  Flex,
  Heading,
  HoverCard,
  Paragraph,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import {
  VIEW_ONLY_COACH_MARK_BANNER,
  ViewChannelPermissionsActions,
} from '~publish/components/ViewOnlyCoachMarks'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import { getViewOnlyScenario } from './viewOnlyScenario'
import type { ViewOnlyChannel } from './viewOnlyScenario'

import styles from './ViewOnlyHintButton.module.css'

type ViewOnlyHintButtonProps = {
  /**
   * Channels whose access decides the badge: it surfaces only when every one
   * of them is view-only. Pass the full set in scope, never a filtered subset.
   */
  channels: ViewOnlyChannel[]
}

const getCopy = (
  channels: ViewOnlyChannel[],
): { lead: string; body: string } | null => {
  if (getViewOnlyScenario(channels) !== 'all') {
    return null
  }

  return channels.length === 1
    ? {
        lead: 'View-only access to this channel',
        body: 'To reply, like, and resolve comments in this channel, ask an admin to update your permissions.',
      }
    : {
        lead: 'View-only access to all the channels',
        body: 'To reply, like, and resolve comments in all channels, ask an admin to update your permissions.',
      }
}

/**
 * The eye + "View only" badge as a focusable trigger. Shared by both the
 * coach-mark and the plain hover-hint states so the trigger is identical
 * regardless of which overlay wraps it. The accessible name comes from the
 * visible badge text so it matches what sighted users see (WCAG 2.5.3 Label
 * in Name); the scenario-specific explanation lives in the hover-card /
 * coach-mark content that opens on hover or focus.
 *
 * Forwards ref + props so it composes with `HoverCard.Trigger` (Radix
 * `asChild`), which injects the pointer/focus handlers that open the hint
 * onto the underlying button.
 */
const ViewOnlyTrigger = forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentPropsWithoutRef<typeof UnstyledButton>, 'children' | 'as'>
>(({ className, ...props }, ref) => (
  <UnstyledButton
    {...props}
    ref={ref}
    className={clsx(styles.trigger, className)}
  >
    <ViewOnlyBadge />
  </UnstyledButton>
))

ViewOnlyTrigger.displayName = 'ViewOnlyTrigger'

/**
 * The persistent hover/focus affordance: the eye icon reveals a hover card
 * explaining the restriction. Opens on both pointer-hover and keyboard focus
 * (Radix HoverCard), same pattern as the channel sync icon next to it.
 */
const ViewOnlyHint = ({
  lead,
  body,
}: {
  lead: string
  body: string
}): JSX.Element => {
  const [open, setOpen] = useState(false)

  return (
    <HoverCard
      open={open}
      onOpenChange={setOpen}
      openDelay="fast"
      closeDelay="fast"
    >
      <HoverCard.Trigger>
        <ViewOnlyTrigger />
      </HoverCard.Trigger>
      <HoverCard.Content
        side="bottom"
        align="center"
        className={styles.content}
      >
        <Flex direction="column" gap="md">
          <Flex direction="column" gap="xs">
            <Heading size="sm">{lead}</Heading>
            <Paragraph size="md">{body}</Paragraph>
          </Flex>
          <Flex justify="end" gap="xs" fullWidth>
            <ViewChannelPermissionsActions
              onDismiss={(): void => setOpen(false)}
            />
          </Flex>
        </Flex>
      </HoverCard.Content>
    </HoverCard>
  )
}

/**
 * The one-time coach mark: spotlights the eye icon and explains the view-only
 * state the first time the actor sees it. Dismissing it persists to the
 * account; after that only {@link ViewOnlyHint} renders. `CoachMark` is itself
 * a HoverCard, so we render either this or the hint — never both on the same
 * anchor — to avoid two overlapping hover cards.
 */
const ViewOnlyCoachMark = ({
  lead,
  body,
  onDismiss,
}: {
  lead: string
  body: string
  onDismiss: () => void
}): JSX.Element => (
  <CoachMark open onDismiss={onDismiss} dismissTimeout={null}>
    <CoachMark.Overlay>
      <CoachMark.Spotlight size="small" />
      <ViewOnlyTrigger />
    </CoachMark.Overlay>
    <CoachMark.Content
      side="bottom"
      align="center"
      showCloseIcon={false}
      className={styles.content}
    >
      <CoachMark.Title>{lead}</CoachMark.Title>
      <CoachMark.Description>{body}</CoachMark.Description>
      <CoachMark.Footer>
        <ViewChannelPermissionsActions onDismiss={onDismiss} />
      </CoachMark.Footer>
    </CoachMark.Content>
  </CoachMark>
)

/**
 * Header affordance for view-only access: the eye + "View only" badge
 * ({@link ViewOnlyBadge}) that explains the restriction. The copy varies by
 * scope (this channel / all the channels) and the whole affordance renders
 * only when every channel is view-only — a header-level
 * badge over mixed access would read as if the actor were view-only
 * everywhere, so that case relies on the per-card indicators instead.
 *
 * On an actor's first visit to a view-only area they see a one-time coach mark
 * spotlighting the badge ({@link ViewOnlyCoachMark}). The dismissal is
 * persisted per account and shared with the Publish view-only coach mark, so
 * once acknowledged anywhere it never returns. The badge still reveals the
 * same copy on hover/focus ({@link ViewOnlyHint}).
 *
 * See `getViewOnlyScenario` and the EPD-998 spec.
 */
export const ViewOnlyHintButton = ({
  channels,
}: ViewOnlyHintButtonProps): JSX.Element | null => {
  const { isActive: coachMarkActive, dismiss: dismissCoachMark } =
    useDismissableBanner(VIEW_ONLY_COACH_MARK_BANNER, { trackDismiss: true })
  const copy = getCopy(channels)

  if (!copy) {
    return null
  }

  return coachMarkActive ? (
    <ViewOnlyCoachMark
      lead={copy.lead}
      body={copy.body}
      onDismiss={dismissCoachMark}
    />
  ) : (
    <ViewOnlyHint lead={copy.lead} body={copy.body} />
  )
}

ViewOnlyHintButton.displayName = 'ViewOnlyHintButton'
